import { useEffect, useState } from 'react';
import adminService from '@wlb/shared/api/admin/adminService';
import type { ReportedContentItem } from '@wlb/shared/api/admin/adminTypes';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../../components/ui/Button';
import { FormError } from '../../components/ui/FormError';
import { Modal } from '../../components/ui/Modal';
import '../../components/ui/ui.css';

interface ReviewReportsDialogProps {
  /** Null keeps the dialog closed. */
  intent: 'approve' | 'reject' | null;
  items: readonly ReportedContentItem[];
  onClose: () => void;
  onReviewed: () => void;
}

export function ReviewReportsDialog({
  intent,
  items,
  onClose,
  onReviewed,
}: ReviewReportsDialogProps) {
  const { t } = useTranslation('admin');
  const [comments, setComments] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (intent) {
      setComments('');
      setSubmitError(null);
    }
  }, [intent]);

  const submit = async () => {
    if (!intent) {
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await adminService.reviewReportedContent({
        isReportApproved: intent === 'approve',
        reviewerComments: comments.trim() || undefined,
        items,
      });
      ToastService.show({
        type: 'success',
        message:
          intent === 'approve'
            ? t('moderation.toasts.approved')
            : t('moderation.toasts.rejected'),
      });
      onReviewed();
      onClose();
    } catch (cause) {
      setSubmitError(getUserFacingErrorMessage(cause, t('common.errorTitle')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={intent != null}
      title={t(
        intent === 'reject' ? 'moderation.review.rejectTitle' : 'moderation.review.approveTitle',
      )}
      description={t('common.selectedCount', { count: items.length })}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? t('common.saving') : t('moderation.review.confirm')}
          </Button>
        </>
      }>
      <FormError variant="banner" message={submitError} />
      <div className="ui-field">
        <label className="ui-field__label" htmlFor="review-comments">
          {t('moderation.review.commentsLabel')}
        </label>
        <textarea
          id="review-comments"
          className="ui-field__control ui-field__control--multiline"
          rows={3}
          placeholder={t('moderation.review.commentsPlaceholder')}
          value={comments}
          onChange={event => setComments(event.target.value)}
        />
      </div>
    </Modal>
  );
}
